import { useEffect, useState } from "react";
import { useLocale } from "@/shared/i18n/locale";
import styles from "./CookieBanner.module.css";

type MarketingChoice = "granted" | "denied" | "unset";

interface PrivacyApi {
  get(): { analytics: "enabled" | "objected"; marketing: MarketingChoice };
  set(choice: {
    analytics?: "enabled" | "objected";
    marketing?: MarketingChoice;
  }): void;
  forget(): void;
}

function privacyApi(): PrivacyApi | undefined {
  return (window as Window & { auPrivacy?: PrivacyApi }).auPrivacy;
}

/** Controle da finalidade de marketing e da oposicao a metricas proprias. */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const locale = useLocale();

  useEffect(() => {
    const isCms = () =>
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/cms");
    if (isCms()) {
      setVisible(false);
      return;
    }
    const sync = () => {
      if (isCms()) {
        setVisible(false);
        return true;
      }
      const privacy = privacyApi();
      if (!privacy) return false;
      setReady(true);
      setVisible(privacy.get().marketing === "unset");
      return true;
    };
    const openPreferences = () => {
      if (isCms()) return;
      setVisible(true);
      setPreferencesOpen(true);
    };
    if (sync()) {
      window.addEventListener("autouprivacyopen", openPreferences);
      return () =>
        window.removeEventListener("autouprivacyopen", openPreferences);
    }
    const timer = window.setInterval(() => {
      if (sync()) window.clearInterval(timer);
    }, 100);
    window.addEventListener("autouprivacyopen", openPreferences);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("autoupacyopen" as string, openPreferences);
      window.removeEventListener("autouprivacyopen", openPreferences);
    };
  }, []);

  const chooseMarketing = (
    marketing: Extract<MarketingChoice, "granted" | "denied">,
  ) => {
    privacyApi()?.set({ marketing });
    setPreferencesOpen(false);
    setVisible(false);
  };

  const opposeAnalytics = () => {
    privacyApi()?.forget();
    setPreferencesOpen(false);
    setVisible(false);
  };

  const isCmsRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/cms");
  if (!visible || isCmsRoute) return null;

  const isEn = locale === "en";

  return (
    <aside className={styles.banner} aria-label={isEn ? "Privacy preferences" : "Preferências de privacidade"}>
      <div className={styles.copy}>
        <p className={styles.title}>{isEn ? "Cookie preferences" : "Preferências de cookies"}</p>
        <p className={styles.text}>
          {isEn ? (
            <>
              We use cookies to improve your experience, analyze site usage, and personalize content. Read our{" "}
              <a
                href="/privacidade/declaracao-de-privacidade.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </>
          ) : (
            <>
              Utilizamos cookies para melhorar sua experiência, analisar o uso do
              site e personalizar conteúdos. Leia nossa{" "}
              <a
                href="/privacidade/declaracao-de-privacidade.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade
              </a>
              .
            </>
          )}
        </p>
      </div>

      {preferencesOpen ? (
        <div className={styles.preferences}>
          <p>
            {isEn ? (
              <>
                <strong>Browsing metrics</strong> help us understand how the
                site is used and improve our services. You can opt out of anonymous
                tracking at any time.
              </>
            ) : (
              <>
                <strong>Métricas de navegação</strong> nos ajudam a entender como o
                site é utilizado e aprimorar nossos serviços. Você pode desativar o
                rastreamento anônimo a qualquer momento.
              </>
            )}
          </p>
          <button
            className={styles.outline}
            type="button"
            onClick={opposeAnalytics}
            disabled={!ready}
          >
            {isEn ? "Disable site metrics" : "Desativar métricas do site"}
          </button>
          <button
            className={styles.close}
            type="button"
            onClick={() => setPreferencesOpen(false)}
          >
            {isEn ? "Close" : "Fechar"}
          </button>
        </div>
      ) : (
        <div className={styles.actions}>
          <button
            className={styles.outline}
            type="button"
            onClick={() => chooseMarketing("denied")}
            disabled={!ready}
          >
            {isEn ? "Decline" : "Recusar"}
          </button>
          <button
            className={styles.accept}
            type="button"
            onClick={() => chooseMarketing("granted")}
            disabled={!ready}
          >
            {isEn ? "Accept all" : "Aceitar todos"}
          </button>
          <button
            className={styles.manage}
            type="button"
            onClick={() => setPreferencesOpen(true)}
          >
            {isEn ? "Preferences" : "Preferências"}
          </button>
        </div>
      )}
    </aside>
  );
}
